import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Bike, Car, Gauge, Scooter, User, X } from 'lucide-react';
import { isValidElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import usePermissions from '../../hooks/usePermissions';

// Detalhe do veículo — side drawer somente-leitura, DESIGN-SYSTEM.md §5.6
// "Side drawer" (slide da direita, largura fixa, dispensa por clique no
// scrim ou no X), mesmo padrão do drawer de `suppliers/View.js`/
// `pages/products/List.js`, preenchido com os cartões `field-card`/`ro-grid`
// de `prototipo-lista-veiculos.html`.
function RoField({ label, value, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            {isValidElement(value) ? (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            ) : (
                <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: value || value === 0 ? 'text.primary' : 'text.disabled', lineHeight: 1.4 }}>
                    {value || value === 0 ? value : '—'}
                </Typography>
            )}
        </Box>
    );
}

function FieldCard({ icon: Icon, title, children }) {
    return (
        <Box sx={{ backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: '16px 18px', mb: '14px', '&:last-child': { mb: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '12px' }}>
                <Icon size={14} color="currentColor" style={{ opacity: 0.55 }} />
                <Typography sx={{ fontSize: '12.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.secondary' }}>
                    {title}
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                {children}
            </Box>
        </Box>
    );
}

const VEHICLE_TYPE_META = {
    car: { label: 'Carro', icon: Car },
    motorcycle: { label: 'Moto', icon: Scooter },
    bicycle: { label: 'Bicicleta', icon: Bike },
};

export default function VehicleViewDrawer({ open, onClose, vehicleId, onRequestEdit }) {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [loading, setLoading] = useState(true);
    const [vehicle, setVehicle] = useState({});
    const [manufacturerName, setManufacturerName] = useState('');

    const fetchVehicle = async (id) => {
        try {
            setLoading(true);
            setManufacturerName('');
            const response = await httpClient.get(`/vehicles/${id}`);
            const data = response.data || {};
            setVehicle(data);

            // `GET /vehicles/{id}` (`VehicleViewResponse`, fv-api) só traz o
            // `manufacturer_id`, não o nome do fabricante (a query não
            // seleciona `m.name`, embora o JOIN exista) — resolvido no
            // cliente reaproveitando o endpoint já existente
            // `GET /vehicles/manufacturers/{type}` (o mesmo usado para
            // popular o <select> em `vehicles/Form.js`), sem alterar o
            // backend.
            if (data.type && data.manufacturer_id) {
                try {
                    const manufacturersResponse = await httpClient.get(`/vehicles/manufacturers/${data.type}`);
                    const list = Array.isArray(manufacturersResponse.data) ? manufacturersResponse.data : [];
                    const found = list.find((m) => String(m.id) === String(data.manufacturer_id));
                    setManufacturerName(found?.name || '');
                } catch (manufacturerError) {
                    console.error('Erro ao carregar fabricante do veículo:', manufacturerError);
                }
            }
        } catch (error) {
            console.error('Erro ao carregar dados do veículo:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (vehicleId && open) {
            fetchVehicle(vehicleId);
        }
    }, [vehicleId, open]);

    const handleClose = () => {
        setVehicle({});
        setManufacturerName('');
        setLoading(true);
        onClose();
    };

    const typeMeta = VEHICLE_TYPE_META[vehicle.type] || { label: vehicle.type || '—', icon: Car };
    const TypeIcon = typeMeta.icon;
    const isBicycle = vehicle.type === 'bicycle';

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 640 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}
        >
            <Box sx={{ p: '20px 22px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, mb: '4px', display: 'flex', alignItems: 'center', gap: '8px' }} noWrap>
                        <TypeIcon size={17} strokeWidth={2} />
                        {loading ? 'Carregando…' : (vehicle.license_plate || vehicle.model || 'Detalhes do veículo')}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        Prévia somente leitura
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small" aria-label="Fechar">
                    <X size={18} strokeWidth={2} />
                </IconButton>
            </Box>

            <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flexDirection: 'column' }}>
                        <CircularProgress size={44} thickness={4} />
                        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                            Carregando informações do veículo...
                        </Typography>
                    </Box>
                ) : (
                    <Fade in={!loading} timeout={400}>
                        <Box>
                            <FieldCard icon={Car} title="Dados do veículo">
                                <RoField label="Tipo" value={<Chip size="small" icon={<TypeIcon size={12} strokeWidth={2.5} />} label={typeMeta.label} sx={{ bgcolor: 'info.bg', color: 'primary.main' }} />} />
                                <RoField label={isBicycle ? 'SN' : 'Placa'} value={isBicycle ? vehicle.chassi : vehicle.license_plate} />
                                <RoField label="Ano de fabricação" value={vehicle.fab_year} />
                                <RoField label="Ano do modelo" value={vehicle.mod_year} />
                                <RoField label="Cor" value={vehicle.color} />
                                <RoField label="Km atual" value={vehicle.kilometer ? `${Number(vehicle.kilometer).toLocaleString('pt-BR')} km` : ''} />
                            </FieldCard>

                            <FieldCard icon={Gauge} title="Fabricante e modelo">
                                <RoField full label="Fabricante" value={manufacturerName} />
                                <RoField full label="Modelo" value={vehicle.model} />
                                {!isBicycle && <RoField full label="Chassi" value={vehicle.chassi} />}
                            </FieldCard>

                            <FieldCard icon={User} title="Cliente">
                                <RoField full label="Nome do cliente" value={vehicle.customer} />
                            </FieldCard>
                        </Box>
                    </Fade>
                )}
            </Box>

            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
                <Button variant="outlined" onClick={handleClose} size="small">Fechar</Button>
                {hasPermission('vehicle.update') && (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            const targetId = vehicleId;
                            handleClose();
                            if (onRequestEdit) {
                                onRequestEdit(targetId);
                            } else {
                                navigate(`/vehicles/edit/${targetId}`);
                            }
                        }}
                    >
                        Editar
                    </Button>
                )}
            </Box>
        </Drawer>
    );
}
